import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { reassignmentsApi, tasksApi } from '../tasks.api';
import type { Hours, Meta, Reassignment } from '../tasks.types';
import { errorMessage, fmtHours, num } from '../tasks.utils';
import DialogShell from './DialogShell';
import EmployeePicker from './EmployeePicker';
import { useCapacityConfirm, wasCancelled } from './useCapacityConfirm';

export interface ReassignContext {
  taskId: number;
  taskCode: string;
  taskTitle: string;
  currentEmployeeId: number | null;
  currentEmployeeName: string | null;
  allocated: Hours;
  actual: Hours;
  remaining: Hours;
}

/**
 * The manager's hand-over form. Two modes:
 *  - `request` given: approving an employee's release request (the reason is theirs);
 *  - otherwise: moving the task directly (the manager gives the reason).
 */
export default function ReassignDialog({
  open,
  meta,
  context,
  request,
  canSkipAcceptance,
  onClose,
  onDone,
}: {
  open: boolean;
  meta: Meta;
  context: ReassignContext;
  request?: Reassignment | null;
  canSkipAcceptance?: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const [to, setTo] = useState<number | null>(null);
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [requireAcceptance, setRequireAcceptance] = useState(true);
  const [busy, setBusy] = useState(false);
  const { run, dialog } = useCapacityConfirm();
  const remaining = num(context.remaining);

  useEffect(() => {
    if (!open) return;
    setTo(request?.toEmployee?.id ?? null);
    setHours(String(remaining));
    setReason('');
    setRemarks('');
    setRequireAcceptance(true);
  }, [open, request, remaining]);

  const transferred = hours === '' ? null : Number(hours);
  const tooMany = transferred !== null && transferred > remaining;

  const submit = async (releaseOnly = false) => {
    if (!releaseOnly && !to) return toast.error('Choose who takes the task over.');
    if (!request && !reason.trim()) return toast.error('Give a reason.');
    setBusy(true);
    try {
      if (request) {
        await run((allowOverCapacity) =>
          reassignmentsApi.approve(request.id, {
            toEmployeeId: releaseOnly ? null : to,
            transferredHours: transferred,
            remarks,
            requireAcceptance,
            allowOverCapacity,
          })
        );
      } else {
        await run((allowOverCapacity) =>
          tasksApi.reassign(context.taskId, {
            toEmployeeId: to as number,
            transferredHours: transferred,
            reason,
            remarks,
            requireAcceptance,
            allowOverCapacity,
          })
        );
      }
      toast.success(releaseOnly ? 'Released to the pool' : 'Task reassigned');
      onDone();
    } catch (e) {
      if (!wasCancelled(e)) toast.error(errorMessage(e, 'Could not reassign'));
    } finally {
      setBusy(false);
    }
  };

  const Fact = ({ label, value }: { label: string; value: string }) => (
    <Box>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography fontWeight={500}>{value}</Typography>
    </Box>
  );

  return (
    <>
      <DialogShell
        open={open}
        title={request ? 'APPROVE RELEASE & REASSIGN' : 'REASSIGN TASK'}
        onClose={onClose}
        onSubmit={() => submit(false)}
        submitLabel={request ? 'Approve & reassign' : 'Reassign'}
        busy={busy}
        disabled={tooMany}
        maxWidth='sm'
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 2 }}>
          <Fact label='Task' value={`${context.taskCode} · ${context.taskTitle}`} />
          <Fact label='Current employee' value={context.currentEmployeeName ?? '—'} />
          <Fact label='Allocated' value={fmtHours(context.allocated)} />
          <Fact label='Actual' value={fmtHours(context.actual)} />
          <Fact label='Remaining' value={fmtHours(context.remaining)} />
        </Box>
        {request?.reason && (
          <Box>
            <Typography variant='caption' color='text.secondary'>
              Reason given by {request.requestedBy?.name}
            </Typography>
            <Typography>{request.reason}</Typography>
          </Box>
        )}
        <EmployeePicker employees={meta.employees} value={to} onChange={setTo} label='Assign to' exclude={context.currentEmployeeId} required={!request} />
        <TextField
          label='Transferred hours'
          type='number'
          size='small'
          inputProps={{ min: 0, step: 0.25, max: remaining }}
          value={hours}
          error={tooMany}
          helperText={tooMany ? `At most ${fmtHours(remaining)} — what is left of the current assignment` : 'Only what is left moves; logged hours stay with the current employee'}
          onChange={(e) => setHours(e.target.value)}
        />
        {!request && <TextField label='Reason' size='small' required value={reason} onChange={(e) => setReason(e.target.value)} />}
        <TextField label='Remarks' size='small' multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        {canSkipAcceptance && (
          <FormControlLabel control={<Switch checked={requireAcceptance} onChange={(e) => setRequireAcceptance(e.target.checked)} />} label='New owner must accept' />
        )}
        {request && (
          <Typography variant='body2' color='text.secondary'>
            Or{' '}
            <Box component='span' sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => !busy && submit(true)}>
              approve the release without a new owner
            </Box>{' '}
            and assign it later.
          </Typography>
        )}
      </DialogShell>
      {dialog}
    </>
  );
}
